import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Camera, X, Plus, Truck, Package, DollarSign, Tag, FileText, MapPin, Info, Sparkles, Loader2, Scale, KeyRound } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { COUNTRY_TO_CURRENCY, getViewerCountry } from '../utils/regionUtils';
import axios from 'axios';
import { detectCountryName } from '../utils/locationUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Weight classes map to actual kg + default dimensions for Shippit quotes
const WEIGHT_CLASSES = [
  { id: 'small', label: 'Small (up to 500g)', kg: 0.5, length: 20, width: 15, height: 10, description: 'Letters, small items, accessories' },
  { id: 'medium', label: 'Medium (500g – 1kg)', kg: 1, length: 30, width: 20, height: 15, description: 'Clothing, books, small toys' },
  { id: 'large', label: 'Large (1 – 3kg)', kg: 3, length: 40, width: 30, height: 20, description: 'Multiple items, shoes, larger toys' },
  { id: 'xlarge', label: 'Extra Large (3 – 5kg)', kg: 5, length: 50, width: 40, height: 25, description: 'Heavy items, electronics' },
  { id: 'heavy', label: 'Heavy (5 – 10kg)', kg: 10, length: 60, width: 40, height: 30, description: 'Large electronics, furniture pieces' },
  { id: 'bulky', label: 'Bulky (10 – 22kg)', kg: 22, length: 80, width: 60, height: 40, description: 'Large/heavy items, may require courier' },
];

const SellPage = () => {
  const navigate = useNavigate();
  const { categories, addProduct, isAuthenticated, user, updateProfile } = useApp();
  const [searchParams] = useSearchParams();
  const isPC = searchParams.get('collection') === 'private';
  const PC_CATEGORIES = ['Toys & Pleasure', 'Lingerie & Sleepwear', 'Wellness & Intimacy', 'Adult Media', 'Adult Services'];
  const [images, setImages] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    originalPrice: '',
    category: '',
    condition: '',
    location: user?.location || 'Australia',
    suburb: user?.suburb || '',
    state: user?.state || '',
    overseas: false,
    country: '',
    shippingAvailable: false,
    weightClass: '',
    pickupAvailable: true,
    listingCurrency: COUNTRY_TO_CURRENCY[getViewerCountry()] || 'AUD'
  });
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const conditions = ['New', 'Like New', 'Excellent', 'Very Good', 'Good', 'Fair'];

  const selectedWeightClass = WEIGHT_CLASSES.find(w => w.id === formData.weightClass);

  // Compress image on the frontend before uploading
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxSize = 1200;
          let { width, height } = img;
          
          if (width > maxSize || height > maxSize) {
            if (width > height) {
              height = Math.round((height * maxSize) / width);
              width = maxSize;
            } else {
              width = Math.round((width * maxSize) / height);
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Compress to JPEG at 70% quality (~100-200KB per image)
          const compressed = canvas.toDataURL('image/jpeg', 0.7);
          resolve(compressed);
        };
        img.onerror = () => reject(new Error('unsupported-format'));
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 5) {
      toast.error('Maximum 5 images allowed');
      return;
    }
    
    for (const file of files) {
      try {
        const compressed = await compressImage(file);
        setImages(prev => [...prev, compressed]);
      } catch (err) {
        console.error('Image compression error:', err);
        toast.error(`"${file.name}" isn't a supported photo format. Please use JPG, PNG or take the photo again.`);
      }
    }
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // AI Analysis function
  const analyzeWithAI = async () => {
    if (images.length === 0) {
      toast.error('Please add an image first');
      return;
    }

    setAiLoading(true);
    try {
      // Get base64 from first image (remove data:image/...;base64, prefix)
      const imageBase64 = images[0].split(',')[1];
      
      const response = await axios.post(`${BACKEND_URL}/api/ai/analyze-product`, {
        image_base64: imageBase64
      });

      if (response.data.success && response.data.analysis) {
        const analysis = response.data.analysis;
        setFormData(prev => ({
          ...prev,
          title: analysis.title || prev.title,
          description: analysis.description || prev.description,
          category: analysis.category || prev.category,
          condition: analysis.condition || prev.condition,
          price: analysis.price_min ? String(Math.round((analysis.price_min + analysis.price_max) / 2)) : prev.price
        }));
        toast.success('AI analysis complete! Review and adjust the suggestions.');
      }
    } catch (error) {
      console.error('AI analysis error:', error);
      toast.error('AI analysis failed. Please fill in details manually.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.error('Please sign in to list an item');
      navigate('/login');
      return;
    }

    if (images.length === 0) {
      toast.error('Please add at least one image');
      return;
    }

    if (!formData.shippingAvailable && !formData.pickupAvailable) {
      toast.error('Please select at least one delivery option');
      return;
    }

    if (formData.shippingAvailable && !formData.weightClass) {
      toast.error('Please select a weight class for shipping');
      return;
    }

    const suburbCountry = detectCountryName(formData.suburb);
    if (!formData.overseas && suburbCountry && suburbCountry !== 'Australia') {
      toast.error(`"${formData.suburb}" looks like a country, not an Australian suburb. Tick "I'm selling from outside Australia" in the Location section.`);
      return;
    }

    if (formData.overseas && !formData.country.trim()) {
      toast.error('Please enter your country');
      return;
    }

    setLoading(true);
    
    try {
      const newProduct = await addProduct({
        title: formData.title,
        description: formData.description,
        price: parseFloat(formData.price),
        originalPrice: formData.originalPrice ? parseFloat(formData.originalPrice) : null,
        images: images.length > 0 ? images : ['https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=500&h=500&fit=crop'],
        category: formData.category,
        condition: formData.condition,
        location: formData.overseas
          ? [formData.suburb.trim(), detectCountryName(formData.country) || formData.country.trim()].filter(Boolean).join(', ')
          : (formData.suburb && formData.state ? `${formData.suburb}, ${formData.state}` : formData.location),
        suburb: formData.suburb,
        state: formData.overseas ? '' : formData.state,
        country: formData.overseas ? (detectCountryName(formData.country) || formData.country.trim()) : 'Australia',
        shippingAvailable: formData.shippingAvailable,
        weightClass: formData.weightClass,
        weight: selectedWeightClass?.kg || 1,
        parcel_length: selectedWeightClass?.length || 30,
        parcel_width: selectedWeightClass?.width || 20,
        parcel_height: selectedWeightClass?.height || 10,
        pickupAvailable: formData.pickupAvailable,
        listingCurrency: formData.listingCurrency,
        shipsInternationally: formData.shipsInternationally || false,
        is_adult: isPC || formData.category === '18+ Adults Only'
      });
      
      if (newProduct) {
        if (!formData.overseas && formData.suburb && formData.state) {
          updateProfile({ suburb: formData.suburb, state: formData.state });
        }
        if (isPC) {
          try {
            const feeRes = await fetch(`${BACKEND_URL}/api/private-collection/listing-fee?user_id=${user.id}&product_id=${newProduct.id}`, { method: 'POST' });
            const fee = await feeRes.json();
            if (fee.live) {
              toast.success('Your piece is live in The Private Collection');
              navigate('/private-collection');
            } else if (fee.checkout_url) {
              toast.success('Listing created — one last step: placement payment');
              window.location.href = fee.checkout_url;
            } else {
              throw new Error(typeof fee.detail === 'string' ? fee.detail : 'Placement payment unavailable');
            }
          } catch (feeErr) {
            toast.error(feeErr.message || 'Listing saved — complete the placement from My Listings');
            navigate('/my-listings');
          }
          return;
        }
        toast.success('Listing created successfully!');
        navigate('/my-listings');
      } else {
        toast.error('Failed to create listing. Please try again.');
      }
    } catch (error) {
      console.error('Error creating listing:', error);
      const msg = error?.message || '';
      if (msg.includes('abort') || msg.includes('timeout') || msg.includes('Timeout')) {
        toast.error('Upload timed out. Try using smaller or fewer images.');
      } else if (msg.includes('413') || msg.includes('too large') || msg.includes('payload')) {
        toast.error('Images are too large. Please use smaller images.');
      } else {
        toast.error(msg || 'Failed to create listing. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title={isPC ? 'Offer a Piece' : 'Create Listing'} showBack showSearch={false}>
      <form onSubmit={handleSubmit} className="pb-4">
        {isPC && (
          <div className="mx-4 mt-3 rounded-sm p-4 flex items-center gap-3" style={{ backgroundColor: '#14100e', border: '1px solid rgba(201,169,98,0.4)' }} data-testid="pc-sell-banner">
            <KeyRound className="w-5 h-5 shrink-0" style={{ color: '#c9a962' }} strokeWidth={1.3} />
            <div>
              <p className="text-[10px] tracking-[0.25em] uppercase" style={{ color: 'rgba(201,169,98,0.8)' }}>The Private Collection · 18+</p>
              <p className="text-xs mt-0.5" style={{ color: '#a89a8c' }}>This piece will be shown only to verified adult members — never on the public marketplace.</p>
            </div>
          </div>
        )}
        {/* Image Upload Section */}
        <div className="bg-white px-4 py-4 mb-2">
          <Label className="flex items-center gap-2 mb-3">
            <Camera className="w-4 h-4" />
            Photos (up to 5)
          </Label>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {images.map((img, index) => (
              <div key={index} className="relative flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden">
                <img src={img} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full"
                >
                  <X className="w-3 h-3" />
                </button>
                {index === 0 && (
                  <span className="absolute bottom-1 left-1 bg-minted-600 text-white text-[10px] px-1.5 py-0.5 rounded">
                    Cover
                  </span>
                )}
              </div>
            ))}
            {images.length < 5 && (
              <label className="flex-shrink-0 w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-minted-500 hover:bg-minted-50 transition-colors">
                <Plus className="w-6 h-6 text-gray-400" />
                <span className="text-xs text-gray-500 mt-1">Add Photo</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
            <Info className="w-3 h-3" />
            First photo will be the cover image
          </p>

          {/* AI Analysis Button - Always visible */}
          <Button
            type="button"
            variant="outline"
            onClick={analyzeWithAI}
            disabled={aiLoading || images.length === 0}
            className="w-full mt-3 border-minted-300 text-minted-700 hover:bg-minted-50 disabled:opacity-50"
          >
            {aiLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Analyzing with AI...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                {images.length === 0 ? 'Add photo to use AI Auto-fill' : 'Auto-fill with AI ✨'}
              </>
            )}
          </Button>
        </div>

        {/* Basic Info */}
        <div className="bg-white px-4 py-4 mb-2 space-y-4">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Item Details
          </h3>
          
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="What are you selling?"
              required
              className="mt-1"
              maxLength={100}
            />
            <p className="text-xs text-gray-500 mt-1 text-right">{formData.title.length}/100</p>
          </div>

          <div>
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Describe your item in detail. Include size, color, brand, condition notes..."
              required
              className="mt-1"
              rows={4}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {isPC
                    ? PC_CATEGORIES.map((name) => (
                        <SelectItem key={name} value={name}>{name}</SelectItem>
                      ))
                    : categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.name}>
                          {cat.icon} {cat.name}
                        </SelectItem>
                      ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Condition *</Label>
              <Select
                value={formData.condition}
                onValueChange={(value) => setFormData({ ...formData, condition: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {conditions.map((cond) => (
                    <SelectItem key={cond} value={cond}>
                      {cond}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div className="bg-white px-4 py-4 mb-2 space-y-4">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Pricing
          </h3>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="price">Price *</Label>
              <div className="flex mt-1 gap-1">
                <select
                  name="listingCurrency"
                  value={formData.listingCurrency}
                  onChange={handleChange}
                  className="h-10 px-2 rounded-md border border-input bg-background text-sm w-20 flex-shrink-0"
                  data-testid="listing-currency-select"
                >
                  <option value="AUD">A$</option>
                  <option value="GBP">£</option>
                  <option value="USD">US$</option>
                  <option value="EUR">€</option>
                  <option value="NZD">NZ$</option>
                  <option value="CAD">CA$</option>
                </select>
                <Input
                  id="price"
                  name="price"
                  type="number"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder="0.00"
                  required
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="originalPrice">Original Price</Label>
              <Input
                id="originalPrice"
                name="originalPrice"
                type="number"
                value={formData.originalPrice}
                onChange={handleChange}
                placeholder="Optional"
                className="mt-1"
                min="0"
                step="0.01"
              />
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="bg-white px-4 py-4 mb-2">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
            <MapPin className="w-4 h-4" />
            Location
          </h3>
          <div className="grid grid-cols-2 gap-3 mb-2">
            <Input
              name="suburb"
              value={formData.suburb}
              onChange={handleChange}
              placeholder={formData.overseas ? 'City (e.g., Los Angeles)' : 'Suburb (e.g., Penrith)'}
              data-testid="sell-suburb-input"
              required
            />
            {formData.overseas ? (
              <Input
                name="country"
                value={formData.country}
                onChange={handleChange}
                placeholder="Country (e.g., USA)"
                data-testid="sell-country-input"
                required
              />
            ) : (
              <select
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="sell-state-select"
                required
              >
                <option value="">State</option>
                {['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 mb-1 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.overseas}
              onChange={(e) => setFormData(prev => ({ ...prev, overseas: e.target.checked, state: '' }))}
              data-testid="sell-overseas-checkbox"
              className="rounded border-gray-300"
            />
            I'm selling from outside Australia
          </label>
          <p className="text-xs text-gray-400">Shown on your listing so local buyers can find you</p>
        </div>

        {/* Delivery Options */}
        <div className="bg-white px-4 py-4 mb-2 space-y-4">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Truck className="w-4 h-4" />
            Delivery Options
          </h3>
          <p className="text-xs text-minted-700 bg-minted-50 border border-minted-100 rounded-lg px-3 py-2">
            🚚 Ship your way — tracked labels, your own courier, or local pickup. Minted never forces a carrier on you.
          </p>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Package className="w-5 h-5 text-minted-600" />
              <div>
                <p className="font-medium text-gray-900">Pickup Available</p>
                <p className="text-sm text-gray-500">Buyer can collect from your location</p>
              </div>
            </div>
            <Switch
              checked={formData.pickupAvailable}
              onCheckedChange={(checked) => setFormData({ ...formData, pickupAvailable: checked })}
              data-testid="pickup-available-toggle"
            />
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Truck className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-gray-900">Shipping Available</p>
                  <p className="text-sm text-gray-500">You'll post the item to buyer</p>
                </div>
              </div>
              <Switch
                checked={formData.shippingAvailable}
                onCheckedChange={(checked) => setFormData({ ...formData, shippingAvailable: checked, weightClass: '' })}
                data-testid="shipping-available-toggle"
              />
            </div>
            
            {formData.shippingAvailable && (
              <div className="ml-8 space-y-3">
                <div>
                  <Label className="flex items-center gap-2">
                    <Scale className="w-4 h-4" />
                    Parcel Size *
                  </Label>
                  <Select
                    value={formData.weightClass}
                    onValueChange={(value) => setFormData({ ...formData, weightClass: value })}
                  >
                    <SelectTrigger className="mt-1" data-testid="weight-class-select">
                      <SelectValue placeholder="Select parcel size" />
                    </SelectTrigger>
                    <SelectContent>
                      {WEIGHT_CLASSES.map((wc) => (
                        <SelectItem key={wc.id} value={wc.id}>
                          {wc.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedWeightClass && (
                  <div className="bg-minted-50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-minted-900">{selectedWeightClass.kg}kg</span>
                      <span className="text-xs text-minted-700">{selectedWeightClass.length}×{selectedWeightClass.width}×{selectedWeightClass.height}cm</span>
                    </div>
                    <p className="text-xs text-minted-700">{selectedWeightClass.description}</p>
                  </div>
                )}
                
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Info className="w-3 h-3" />
                  Shipping cost calculated at checkout based on buyer's location
                </p>
                
                {/* International Shipping Toggle */}
                <div className="border-t pt-3 mt-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Ships Internationally</p>
                      <p className="text-xs text-gray-500">Enable for UK, US, EU buyers</p>
                    </div>
                    <Switch
                      checked={formData.shipsInternationally || false}
                      onCheckedChange={(checked) => setFormData({ ...formData, shipsInternationally: checked })}
                      data-testid="ships-internationally-toggle"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="px-4 py-4">
          <Button
            type="submit"
            className="w-full bg-minted-600 hover:bg-minted-700 h-12 text-base"
            disabled={loading}
          >
            {loading ? 'Creating Listing...' : 'Publish Listing'}
          </Button>
        </div>
      </form>
    </Layout>
  );
};

export default SellPage;
